import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "lib/utils"

const buttonVariants = cva(
  "cursor-pointer inline-flex items-center justify-center whitespace-nowrap rounded-xl text-sm font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-95 border-0 outline-none appearance-none",
  {
      variants: {
        variant: {
          default: "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/40 hover:from-blue-700 hover:to-blue-800 border-0",
          destructive: "bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg shadow-red-500/25 hover:shadow-xl hover:shadow-red-500/40 hover:from-red-600 hover:to-red-700",
          outline: "bg-white text-blue-600 hover:bg-blue-50 shadow-sm hover:shadow-md",
          secondary: "bg-gray-100 text-gray-900 hover:bg-gray-200 shadow-sm hover:shadow-md",
          ghost: "text-gray-600 hover:bg-gray-100 hover:text-gray-900",
          link: "text-blue-600 underline-offset-4 hover:underline font-medium",
          success: "bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg shadow-green-500/25 hover:shadow-xl hover:shadow-green-500/40 hover:from-green-600 hover:to-green-700",
          warning: "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/40 hover:from-orange-600 hover:to-orange-700",
          gradient: "bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 text-white shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/40 hover:scale-105",
        },
      size: {
        default: "h-11 px-6 py-2.5",
        sm: "h-9 px-4 py-2 text-xs",
        lg: "h-12 px-8 py-3 text-base",
        xl: "h-14 px-10 py-4 text-lg",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const PrezemButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, style, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    
    // outline variant일 때 primary 색상의 border 적용
    const outlineStyle = variant === "outline" ? {
      border: '1px solid rgb(37, 99, 235)', // blue-600 (primary color)
      transition: 'all 0.2s ease',
      ...style
    } : style

    const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (variant === "outline") {
        e.currentTarget.style.borderColor = 'rgb(29, 78, 216)' // blue-700 (darker)
      }
      if (props.onMouseEnter) props.onMouseEnter(e)
    }

    const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (variant === "outline") {
        e.currentTarget.style.borderColor = 'rgb(37, 99, 235)' // blue-600 (back to original)
      }
      if (props.onMouseLeave) props.onMouseLeave(e)
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        style={outlineStyle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={ref}
        {...props}
      />
    )
  }
)
PrezemButton.displayName = "PrezemButton"

export { PrezemButton, buttonVariants };
